import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom'; 

const getCardImageUrl = (card, type) => {
  if (!card?.acf) return null;
  if (type === "workAreas") return card.acf.imgUrl?.url || null;
  if (type === "people") return card.acf.photourl?.url || null;
  if (type === "projects") return card.acf.projectimg?.url || null;
  return null;
};

const ImageRotator = (props) => {
  const {type, cardArray = [], trigger, isActive} = props;
  const navigate = useNavigate();
  const length = cardArray.length;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [, setReadyIndexes] = useState(() => new Set(length > 0 ? [0] : []));

  // Keep ready set in sync when column contents change
  useEffect(() => {
    setReadyIndexes(new Set(length > 0 ? [0] : []));
    setCurrentIndex(0);
  }, [length, type]);

  // Off-DOM preload for any index; mark ready when loaded
  const preloadIndex = (index) => {
    if (length === 0) return;
    const normalized = ((index % length) + length) % length;
    const url = getCardImageUrl(cardArray[normalized], type);
    if (!url) {
      setReadyIndexes((prev) => new Set(prev).add(normalized));
      return;
    }
    const img = new window.Image();
    img.onload = () => {
      setReadyIndexes((prev) => {
        if (prev.has(normalized)) return prev;
        const next = new Set(prev);
        next.add(normalized);
        return next;
      });
    };
    img.onerror = () => {
      setReadyIndexes((prev) => {
        if (prev.has(normalized)) return prev;
        const next = new Set(prev);
        next.add(normalized);
        return next;
      });
    };
    img.src = url;
  };

  // Always warm the next frame
  useEffect(() => {
    if (length <= 1) return;
    preloadIndex(currentIndex + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, length, type, cardArray]);

  // Advance when this column is active
  useEffect(() => {
    if (!isActive || length === 0) return;
    setCurrentIndex((prevIndex) => (prevIndex + 1) % length);
  }, [trigger, length, isActive]);

  const layerStyle = (index, baseZ) => ({
    opacity: currentIndex === index ? 1 : 0,
    transition: 'opacity 1s ease-in-out',
    zIndex: baseZ - index,
    pointerEvents: currentIndex === index ? 'auto' : 'none',
  });

  if (length === 0) {
    return <div className="home-card-item" />;
  }

  // Keep prev + current + next mounted so opacity can crossfade (unmounting = jump).
  const prevIndex = (currentIndex - 1 + length) % length;
  const nextIndex = (currentIndex + 1) % length;
  const indexesToRender = length === 1
    ? [0]
    : [...new Set([prevIndex, currentIndex, nextIndex])];

  const navigateForType = () => {
    if (type === "workAreas") navigate(`/workareas/`);
    else if (type === "people") navigate('/people');
    else if (type === "projects") navigate(`/projects/`);
  };

  const baseZ = type === "workAreas" ? 50 : 40;

  const images = indexesToRender.map((index) => {
    const card = cardArray[index];
    const url = getCardImageUrl(card, type);

    if (type === "workAreas") {
      return (
        <div
          className="hover-tint wa home-rotator-layer"
          key={index}
          style={layerStyle(index, baseZ)}
          onClick={navigateForType}
        >
          <img
            className="homepage-wa-item"
            src={url}
            onLoad={() => {
              setReadyIndexes((prev) => {
                if (prev.has(index)) return prev;
                const next = new Set(prev);
                next.add(index);
                return next;
              });
            }}
            alt=""
          />
        </div>
      );
    }

    if (type === "people") {
      return (
        <div
          className="hover-tint people home-rotator-layer"
          key={index}
          style={layerStyle(index, baseZ)}
          onClick={navigateForType}
        >
          <img
            className="homepage-people-item"
            src={url}
            onLoad={() => {
              setReadyIndexes((prev) => {
                if (prev.has(index)) return prev;
                const next = new Set(prev);
                next.add(index);
                return next;
              });
            }}
            alt=""
          />
        </div>
      );
    }

    if (type === "projects") {
      return (
        <div
          className="homepage-projects-item"
          key={index}
          style={layerStyle(index, baseZ)}
          onClick={navigateForType}
        >
          <span className="home-project-img-bg" style={{width:"108%"}}>
            <img
              className="project-img background-tint"
              src={url}
              onLoad={() => {
                setReadyIndexes((prev) => {
                  if (prev.has(index)) return prev;
                  const next = new Set(prev);
                  next.add(index);
                  return next;
                });
              }}
              alt=""
            />
          </span>
        </div>
      );
    }

    return null;
  });

  return (
    <div className="home-card-item">
      {images}
    </div>
  );
};

export default ImageRotator;
